import React, { useEffect, useMemo, useRef, useState } from "react";
import { FiLoader, FiTrash2, FiUploadCloud, FiX, FiZap, FiFilm, FiInfo, FiUser, FiCamera, FiCpu, FiPlay, FiPause, FiEdit3, FiCheck } from "react-icons/fi";
import { apiRequest, errorMessage } from "../api";
import { useJobHistory, formatSeconds, formatSize } from "./audioShared";
import { StudioPanel, LANGUAGES, RATIOS, inputClass, labelClass, card, Pills, Slider } from "./VideoStudioPanel";
import LivePreview from "./avatar/LivePreview";
import PresenterThumb from "./avatar/PresenterThumb";
import FaceMarker, { detectFace } from "./avatar/FaceMarker";
import StageControls from "./avatar/StageControls";
import { PRESENTERS, PRESENTER_IDS } from "../video/presenters";
import { DEFAULT_MARKS, marksAreValid } from "../video/puppet";
import { DEFAULT_STAGE } from "../video/stage";
import { formatDuration, speakingSeconds } from "../video/avatar";
import { loadImage } from "../video/studio";
import { takeHandoff } from "../video/script";

const MAX_PHOTO_BYTES = 10 * 1024 * 1024;
const MAX_SCRIPT = 3500;
const SCRIPT_IDEAS = [
  "Hello and welcome! I'm so glad you're here. Today I'll show you what we do, how it works and how you can get started in just a few minutes. Let's begin.",
  "Welcome to our shop. This week every new customer gets a free gift with their first order. Come and visit us, or order online. We can't wait to meet you.",
  "Good morning, team. Here is a quick update on this week: what went well, what we will focus on next, and how each of you can help. Thank you for everything you do.",
];
const TONES = [["friendly", "Friendly"], ["professional", "Professional"], ["energetic", "Energetic"], ["calm", "Calm"]];
const AI_IDEAS = ["a friendly woman in her thirties with shoulder-length brown hair, wearing a navy blazer", "a confident man in his forties with short dark hair and a light grey suit", "a smiling young woman with glasses and a green sweater", "a kind older man with grey hair and a white shirt"];

// Whether a picture has transparent parts (a cut-out person): looks at a small copy of it.
function detectAlpha(img) {
  try {
    const canvas = document.createElement("canvas");
    canvas.width = 48;
    canvas.height = 48;
    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    ctx.drawImage(img, 0, 0, 48, 48);
    const data = ctx.getImageData(0, 0, 48, 48).data;
    for (let i = 3; i < data.length; i += 4) if (data[i] < 245) return true;
  } catch { /* an unreadable picture is treated as opaque */ }
  return false;
}

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

export default function AvatarVideoTool() {
  const { clientId, history: everything, loading, elapsed, add, remove } = useJobHistory("video");
  const history = useMemo(() => everything.filter((job) => job.quality === "avatar"), [everything]);
  const running = history.find((job) => job.status === "queued" || job.status === "processing");
  const latest = history[0];

  // presenter
  const [source, setSource] = useState("studio");         // studio | photo | ai
  const [presenterId, setPresenterId] = useState(PRESENTER_IDS[0]);
  const [photo, setPhoto] = useState(null);               // { file, url, img, hasAlpha }
  const [marks, setMarks] = useState(DEFAULT_MARKS);
  const [dragging, setDragging] = useState(false);
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [aiError, setAiError] = useState("");
  // script and voice
  const [script, setScript] = useState("");
  const [topic, setTopic] = useState("");
  const [tone, setTone] = useState("friendly");
  const [length, setLength] = useState(45);
  const [writing, setWriting] = useState(false);
  const [writerEngine, setWriterEngine] = useState("");
  const [voices, setVoices] = useState([]);
  const [language, setLanguage] = useState("en");
  const [voice, setVoice] = useState("");
  const [rate, setRate] = useState(0);
  // stage
  const [stage, setStage] = useState(DEFAULT_STAGE);
  const [background, setBackground] = useState(null);     // { file, url, img }
  const [name, setName] = useState("");
  const [role, setRole] = useState("");
  const [ratio, setRatio] = useState("16:9");
  const [talking, setTalking] = useState(true);
  // the job
  const [title, setTitle] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [fieldError, setFieldError] = useState("");
  const [makeError, setMakeError] = useState("");
  const [viewId, setViewId] = useState(null);
  const [detail, setDetail] = useState(null);
  const voiceChosen = useRef(false);

  // a script sent here from the Video Script Generator
  useEffect(() => {
    const sent = takeHandoff("avatar");
    if (sent) { setScript(sent.text); if (sent.title) setTitle(sent.title.slice(0, 80)); }
  }, []);
  const photoInput = useRef(null);
  const resultRef = useRef(null);
  const urls = useRef([]);

  useEffect(() => {
    let alive = true;
    const created = urls.current;                  // the same list for the whole life of the page: every picture address made on it is released when it is left
    apiRequest("/core/tts/voices/").then(({ ok, data }) => { if (alive && ok) setVoices(data.voices || []); });
    return () => { alive = false; created.forEach((u) => URL.revokeObjectURL(u)); };
  }, []);

  const languages = useMemo(() => [...new Set(voices.map((v) => v.language))].filter((l) => LANGUAGES[l]), [voices]);
  const voicesHere = useMemo(() => voices.filter((v) => v.language === language), [voices, language]);
  const presenterGender = source === "studio" ? (["sara", "zainab", "emma", "noor"].includes(presenterId) ? "female" : "male") : "";
  // the voice follows the presenter (a woman gets a woman's voice) until the visitor chooses a voice themselves
  useEffect(() => {
    if (!voicesHere.length) return;
    if (voiceChosen.current && voicesHere.some((v) => v.id === voice)) return;
    const wanted = presenterGender || "female";
    const pick = (voicesHere.find((v) => v.gender === wanted) || voicesHere[0]).id;
    if (pick !== voice) setVoice(pick);
  }, [voicesHere, voice, presenterGender]);

  useEffect(() => { if (latest && latest.status === "completed") setViewId(latest.id); }, [latest?.id, latest?.status]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!viewId) { setDetail(null); return undefined; }
    let alive = true;
    apiRequest(`/core/voice-clone/${viewId}/?client_id=${encodeURIComponent(clientId)}`).then(({ ok, data }) => { if (alive) setDetail(ok ? data : null); });
    return () => { alive = false; };
  }, [viewId, clientId]);

  // ---------------------------------------------------------------------------------------- the photo
  const takePhoto = async (file) => {
    setFieldError("");
    if (!file) return;
    if (!file.type.startsWith("image/")) return setFieldError(`"${file.name}" is not a picture (use JPG, PNG or WebP).`);
    if (file.size > MAX_PHOTO_BYTES) return setFieldError(`"${file.name}" is larger than 10 MB.`);
    const url = URL.createObjectURL(file);
    urls.current.push(url);
    try {
      const img = await loadImage(url);
      if (Math.min(img.naturalWidth, img.naturalHeight) < 400) return setFieldError("That picture is small. Use one at least 400 pixels wide so the face is sharp.");
      setPhoto({ file, url, img, hasAlpha: detectAlpha(img) });
      setMarks((await detectFace(img)) || DEFAULT_MARKS);
      return setSource((s) => (s === "ai" ? "ai" : "photo"));
    } catch {
      return setFieldError("That picture could not be opened. Use a JPG, PNG or WebP file.");
    }
  };

  const takeBackground = async (file) => {
    setFieldError("");
    if (!file) return;
    if (!file.type.startsWith("image/") || file.size > MAX_PHOTO_BYTES) return setFieldError("Use a picture (JPG, PNG or WebP) of up to 10 MB for the background.");
    const url = URL.createObjectURL(file);
    urls.current.push(url);
    try {
      setBackground({ file, url, img: await loadImage(url) });
      setStage((s) => ({ ...s, background: "image" }));
    } catch { setFieldError("That picture could not be opened."); }
  };
  const clearBackground = () => { setBackground(null); setStage((s) => (s.background === "image" ? { ...s, background: "studio" } : s)); };

  // ---------------------------------------------------------------------------------------- an AI-made presenter
  const makeAiPresenter = async () => {
    setAiError("");
    setAiBusy(true);
    try {
      const form = new FormData();
      form.append("client_id", clientId);
      form.append("kind", "image");
      form.append("prompt", `${aiPrompt.trim()}, front view, looking straight at the camera, calm neutral face, mouth closed, head and shoulders portrait, plain simple background`);
      form.append("negative", "side view, profile, turned head, hand over face, sunglasses, open mouth, text, watermark");
      form.append("style", "photo");
      form.append("ratio", "3:4");
      form.append("count", "1");
      const created = await apiRequest("/core/voice-clone/", { method: "POST", body: form });
      if (!created.ok) throw new Error(created.data?.prompt || errorMessage(created.data));
      let job = created.data;
      for (let n = 0; n < 120 && (job.status === "queued" || job.status === "processing"); n += 1) {
        await sleep(3000);
        const polled = await apiRequest(`/core/voice-clone/${job.id}/?client_id=${encodeURIComponent(clientId)}`);
        if (polled.ok) job = polled.data;
      }
      if (job.status !== "completed" || !job.images?.[0]) throw new Error(job.error_message || "The picture could not be drawn right now. Please try again in a minute.");
      const blob = await (await fetch(job.images[0].url)).blob();
      await takePhoto(new File([blob], "ai-presenter.png", { type: blob.type || "image/png" }));
      setSource("ai");
    } catch (e) {
      setAiError(e.message || "The presenter could not be made. Please try again.");
    }
    setAiBusy(false);
  };

  // ---------------------------------------------------------------------------------------- the script
  const writeScript = async () => {
    setFieldError("");
    setWriting(true);
    const { ok, data } = await apiRequest("/core/video/avatar-script/", { method: "POST", body: { topic, seconds: length, tone, language } });
    setWriting(false);
    if (!ok) return setFieldError(data?.topic || errorMessage(data));
    setScript(data.script);
    return setWriterEngine(data.engine);
  };

  // ---------------------------------------------------------------------------------------- the video
  const usingPhoto = source !== "studio" && !!photo;
  const avatar = useMemo(() => ({ type: usingPhoto ? "photo" : "builtin", presenter: presenterId, marks, stage, name, role }), [usingPhoto, presenterId, marks, stage, name, role]);
  const seconds = speakingSeconds(script);
  const marksOk = !usingPhoto || marksAreValid(marks);
  const canMake = script.trim().length >= 12 && !!voice && marksOk && !submitting && !running;

  const makeVideo = async () => {
    setError("");
    setFieldError("");
    setMakeError("");
    setSubmitting(true);
    const form = new FormData();
    form.append("client_id", clientId);
    form.append("kind", "video");
    form.append("mode", "avatar");
    form.append("title", title.trim());
    form.append("script", script.trim());
    form.append("voice", voice);
    form.append("rate", String(rate));
    form.append("ratio", ratio);
    form.append("avatar", JSON.stringify({ presenter: presenterId, name: name.trim(), role: role.trim(), marks: usingPhoto ? marks : undefined, stage }));
    if (usingPhoto) form.append("photo", photo.file);
    if (stage.background === "image" && background) form.append("background", background.file);
    const { ok, data } = await apiRequest("/core/voice-clone/", { method: "POST", body: form });
    setSubmitting(false);
    if (!ok) { setMakeError(data?.prompt || errorMessage(data)); return; }
    setViewId(null);
    add(data);
    setTimeout(() => resultRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 100);
  };

  const view = detail && detail.status === "completed" ? detail : null;
  const segments = view?.segments && !Array.isArray(view.segments) ? view.segments : {};
  const tab = (key, label, Icon) => (
    <button type="button" onClick={() => setSource(key)} aria-pressed={source === key}
      className={`flex-1 min-w-[8.5rem] flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold border transition-colors ${source === key ? "bg-accent-light text-navy border-accent-light" : "bg-white/5 border-navy-border text-gray-300 hover:border-accent-light"}`}><Icon size={15} /> {label}</button>
  );

  return (
    <div className="max-w-6xl mx-auto px-5 md:px-10 py-14">
      <h2 className="text-2xl md:text-3xl font-bold text-center mb-3">AI Avatar &amp; Talking Head Videos</h2>
      <p className="text-center text-gray-400 max-w-3xl mx-auto mb-9">
        Choose a presenter - or use your own photo - and type what they should say. A natural voice reads it and the presenter speaks it to the camera: their lips follow the real sound of the voice, they blink, nod and move like a person talking.
        Save it as an MP4 and use it for welcomes, product videos, training, news and social media.
      </p>

      <div className="grid lg:grid-cols-[1.25fr_1fr] gap-6 items-start">
        <div className={`${card} space-y-9`}>
          {/* 1 - the presenter */}
          <div>
            <span className={labelClass}>1. Your presenter</span>
            <div className="flex flex-wrap gap-2 mb-5" role="group" aria-label="Where the presenter comes from">
              {tab("studio", "Studio presenters", FiUser)}
              {tab("photo", "My photo", FiCamera)}
              {tab("ai", "Create with AI", FiCpu)}
            </div>

            {source === "studio" && (
              <div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {PRESENTER_IDS.map((id) => (
                    <button key={id} type="button" onClick={() => setPresenterId(id)} aria-pressed={presenterId === id} aria-label={PRESENTERS[id].name}
                      className={`relative rounded-xl overflow-hidden border-2 transition-colors ${presenterId === id ? "border-accent-light" : "border-transparent hover:border-white/30"}`}>
                      <PresenterThumb id={id} size={150} className="w-full h-auto" />
                      <span className="absolute bottom-0 inset-x-0 bg-black/55 text-xs font-semibold py-1">{PRESENTERS[id].name}</span>
                      {presenterId === id && <span className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-accent-light text-navy flex items-center justify-center"><FiCheck size={12} /></span>}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-3">Studio presenters are illustrated characters, drawn fresh for every frame, so they look sharp at any size and never run out of free capacity.</p>
              </div>
            )}

            {source === "photo" && (
              <div>
                {!photo ? (
                  <div onClick={() => photoInput.current?.click()} onDragOver={(e) => { e.preventDefault(); setDragging(true); }} onDragLeave={() => setDragging(false)}
                    onDrop={(e) => { e.preventDefault(); setDragging(false); takePhoto(e.dataTransfer.files[0]); }} role="button" tabIndex={0} onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && photoInput.current?.click()}
                    className={`cursor-pointer rounded-xl border-2 border-dashed px-4 py-12 text-center transition-colors ${dragging ? "border-accent-light bg-accent/10" : "border-navy-border hover:border-accent-light/70"}`}>
                    <FiUploadCloud className="mx-auto text-accent-light" size={30} />
                    <p className="mt-2 text-sm text-gray-200">Click to choose a photo of the presenter, or drop it here</p>
                    <p className="text-xs text-gray-500 mt-1">A face looking at the camera, in JPG, PNG or WebP (up to 10 MB). A PNG with a transparent background puts the person on your stage.</p>
                  </div>
                ) : (
                  <div>
                    <div className="flex items-center justify-between mb-3 gap-3">
                      <span className="text-sm text-gray-200 truncate" title={photo.file.name}>{photo.file.name} <span className="text-gray-500">· {formatSize(photo.file.size)}{photo.hasAlpha ? " · cut-out" : ""}</span></span>
                      <button type="button" onClick={() => { setPhoto(null); setMarks(DEFAULT_MARKS); }} className="text-xs text-gray-400 hover:text-red-400 inline-flex items-center gap-1 shrink-0"><FiX size={13} /> Use another photo</button>
                    </div>
                    <p className="text-sm text-gray-300 mb-3"><FiEdit3 className="inline -mt-0.5 mr-1" size={14} />Drag the four dots onto the <strong>centre of each eye</strong> and the <strong>two corners of the mouth</strong>. The preview on the right shows the lips moving - adjust until it looks natural.</p>
                    <FaceMarker src={photo.url} marks={marks} onChange={setMarks} />
                  </div>
                )}
                <input ref={photoInput} type="file" accept="image/*" className="hidden" onChange={(e) => { takePhoto(e.target.files?.[0]); e.target.value = ""; }} />
                <p className="text-xs text-gray-500 mt-3">Use only photos of people who agree to it. The result is an AI-animated picture, not real footage of them speaking.</p>
              </div>
            )}

            {source === "ai" && (
              <div>
                {!photo ? (
                  <>
                    <label htmlFor="av-ai" className={labelClass}>Describe your presenter</label>
                    <textarea id="av-ai" className={`${inputClass} min-h-[84px]`} value={aiPrompt} maxLength={300} dir="auto" onChange={(e) => setAiPrompt(e.target.value)} placeholder="e.g. a friendly woman in her thirties with shoulder-length brown hair, wearing a navy blazer" />
                    <div className="flex flex-wrap gap-2 mt-3">
                      {AI_IDEAS.map((idea) => <button key={idea} type="button" onClick={() => setAiPrompt(idea)} className="px-3 py-1 rounded-full text-xs border border-navy-border text-gray-300 hover:border-accent-light text-start">{idea.length > 50 ? `${idea.slice(0, 48)}...` : idea}</button>)}
                    </div>
                    {aiError && <p className="text-sm text-amber-300 mt-3">{aiError}</p>}
                    <button type="button" onClick={makeAiPresenter} disabled={aiBusy || aiPrompt.trim().length < 8}
                      className="mt-4 w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-brand-gradient text-white font-semibold disabled:opacity-50 disabled:cursor-not-allowed">
                      {aiBusy ? <><FiLoader className="animate-spin" /> Drawing your presenter - about a minute...</> : <><FiZap /> Create my presenter</>}
                    </button>
                    <p className="text-xs text-gray-500 mt-3">An AI draws a front-facing portrait; you then mark the face and it speaks. If the free picture service is busy it may take a few tries.</p>
                  </>
                ) : (
                  <div>
                    <div className="flex items-center justify-between mb-3 gap-3">
                      <span className="text-sm text-gray-200">Your AI presenter is ready.</span>
                      <button type="button" onClick={() => { setPhoto(null); setMarks(DEFAULT_MARKS); }} className="text-xs text-gray-400 hover:text-red-400 inline-flex items-center gap-1"><FiX size={13} /> Make another</button>
                    </div>
                    <p className="text-sm text-gray-300 mb-3"><FiEdit3 className="inline -mt-0.5 mr-1" size={14} />Drag the four dots onto the <strong>centre of each eye</strong> and the <strong>corners of the mouth</strong>.</p>
                    <FaceMarker src={photo.url} marks={marks} onChange={setMarks} />
                  </div>
                )}
              </div>
            )}
            {fieldError && !script && <p className="text-sm text-amber-300 mt-3">{fieldError}</p>}
          </div>

          {/* 2 - the script */}
          <div>
            <div className="flex items-center justify-between mb-2.5">
              <label htmlFor="av-script" className={labelClass.replace("mb-2.5", "")}>2. What they say</label>
              <span className="text-xs text-gray-500">{script.length} / {MAX_SCRIPT}{seconds ? ` · about ${formatDuration(seconds)}` : ""}</span>
            </div>
            <textarea id="av-script" className={`${inputClass} min-h-[150px] leading-relaxed`} value={script} dir="auto" maxLength={MAX_SCRIPT} onChange={(e) => setScript(e.target.value)} placeholder="Type or paste the script here, or let the AI write it below." />
            <div className="flex flex-wrap gap-2 mt-3">
              {SCRIPT_IDEAS.map((idea) => <button key={idea} type="button" onClick={() => setScript(idea)} className="px-3 py-1 rounded-full text-xs border border-navy-border text-gray-300 hover:border-accent-light text-start">{idea.length > 46 ? `${idea.slice(0, 44)}...` : idea}</button>)}
            </div>
            <div className="mt-5 rounded-xl border border-navy-border bg-navy/60 p-4 space-y-3">
              <p className="text-sm font-semibold"><FiZap className="inline -mt-0.5 mr-1 text-accent-light" size={14} />Let the AI write the script</p>
              <input aria-label="What the video is about" className={inputClass} value={topic} maxLength={300} dir="auto" onChange={(e) => setTopic(e.target.value)} placeholder="What is it about? e.g. Welcome new customers to our bakery and tell them about our opening offer" />
              <div className="grid sm:grid-cols-[1.4fr_1fr] gap-4 items-end">
                <div><span className={labelClass}>Tone</span><Pills label="Tone" value={tone} onChange={setTone} options={TONES} /></div>
                <Slider label="Length" value={length} min={15} max={180} step={5} suffix=" s" onChange={setLength} />
              </div>
              <button type="button" onClick={writeScript} disabled={writing || topic.trim().length < 6} className="px-5 py-2.5 rounded-full border border-white/20 hover:border-accent-light text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50">
                {writing ? <><FiLoader className="animate-spin" /> Writing...</> : <><FiZap size={14} /> {script ? "Write it again" : "Write the script"}</>}
              </button>
              {writerEngine && <span className="ml-3 text-xs text-gray-500">Written by {writerEngine}. Read it and change anything you like.</span>}
              {fieldError && script && <p className="text-sm text-amber-300">{fieldError}</p>}
            </div>
          </div>

          {/* 3 - the voice */}
          <div>
            <span className={labelClass}>3. The voice</span>
            <div className="grid sm:grid-cols-3 gap-3">
              <div>
                <label htmlFor="av-lang" className="text-xs text-gray-400 block mb-1.5">Language</label>
                <select id="av-lang" className={inputClass} value={language} onChange={(e) => { setLanguage(e.target.value); voiceChosen.current = false; setVoice(""); }}>
                  {languages.length ? languages.map((l) => <option key={l} value={l}>{LANGUAGES[l]}</option>) : <option value="en">English</option>}
                </select>
              </div>
              <div>
                <label htmlFor="av-voice" className="text-xs text-gray-400 block mb-1.5">Voice</label>
                <select id="av-voice" className={inputClass} value={voice} onChange={(e) => { voiceChosen.current = true; setVoice(e.target.value); }}>
                  {voicesHere.map((v) => <option key={v.id} value={v.id}>{v.name.split(" - ")[0]}{v.accent_label ? ` (${v.accent_label})` : ""}, {v.gender}</option>)}
                </select>
              </div>
              <div className="self-end"><Slider label="Speaking speed" value={rate} min={-30} max={40} step={5} suffix="%" onChange={setRate} /></div>
            </div>
          </div>

          {/* 4 - the stage */}
          <div>
            <span className={labelClass}>4. The stage</span>
            <StageControls stage={stage} onStage={(patch) => setStage((s) => ({ ...s, ...patch }))} name={name} role={role} onName={setName} onRole={setRole}
              background={background} onPickBackground={takeBackground} onClearBackground={clearBackground} idPrefix="av" stageUsed={!usingPhoto || !!photo?.hasAlpha} />
          </div>
        </div>

        {/* the preview and the button */}
        <div className="lg:sticky lg:top-24 space-y-4">
          <div className={card}>
            <div className="flex items-center justify-between mb-3">
              <span className={labelClass.replace("mb-2.5", "")}>Live preview</span>
              <button type="button" onClick={() => setTalking((t) => !t)} className="text-xs text-gray-300 hover:text-white inline-flex items-center gap-1.5 border border-white/20 rounded-full px-3 py-1.5" aria-pressed={talking}>
                {talking ? <><FiPause size={12} /> Stop the lips</> : <><FiPlay size={12} /> Move the lips</>}
              </button>
            </div>
            <LivePreview avatar={avatar} photo={usingPhoto ? photo : null} background={background} ratio={ratio} talking={talking} />
            <div className="mt-4"><span className={labelClass}>Video shape</span>
              <Pills label="Video shape" value={ratio} onChange={setRatio} options={RATIOS.map(([k, l]) => [k, l.replace("Landscape ", "").replace("Tall ", "").replace("Square ", "")])} />
            </div>
            <div className="mt-4"><label htmlFor="av-title" className={labelClass}>Video title <span className="normal-case tracking-normal text-gray-500 font-normal">(optional)</span></label>
              <input id="av-title" className={inputClass} value={title} maxLength={80} dir="auto" onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Welcome to Aali Bakery" /></div>
            {usingPhoto && !marksOk && <p className="text-sm text-amber-300 mt-3">The dots do not look like a face yet: the eyes must be above the mouth, left eye on the left.</p>}
            <button type="button" onClick={makeVideo} disabled={!canMake}
              className="w-full mt-5 flex items-center justify-center gap-2 px-6 py-4 rounded-full bg-brand-gradient text-white font-semibold shadow-lg shadow-accent/20 disabled:opacity-50 disabled:cursor-not-allowed">
              {submitting ? <><FiLoader className="animate-spin" /> Sending...</> : running ? <><FiLoader className="animate-spin" /> Recording the voice...</> : <><FiFilm /> Make the video{seconds ? ` (${formatDuration(seconds)})` : ""}</>}
            </button>
            {makeError && <p className="text-sm text-amber-200 bg-amber-500/10 border border-amber-500/30 rounded-xl px-4 py-3 mt-3" role="alert">{makeError}</p>}
            {!script.trim() && <p className="text-xs text-gray-500 text-center mt-2">Write the script first</p>}
            <p className="text-xs text-gray-500 text-center mt-3">The voice is recorded in a few seconds. Then you can watch the video, change the stage, and save it as an MP4.</p>
          </div>
        </div>
      </div>

      <div ref={resultRef} />
      {error && <p className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-xl px-5 py-4 mt-6">{error}</p>}
      {running && (
        <div className="rounded-2xl bg-white/5 border border-accent-light/30 p-6 mt-6 flex items-center gap-3">
          <FiLoader className="animate-spin text-accent-light shrink-0" size={22} />
          <div><p className="font-semibold">Recording the voice... {formatSeconds(elapsed)}{running.progress && <span className="text-gray-400 font-normal"> · {running.progress}</span>}</p><p className="text-sm text-gray-400">Each part of the script is recorded by the voice. You can leave this page open.</p></div>
        </div>
      )}
      {latest && latest.status === "failed" && !running && (
        <div className="rounded-2xl bg-red-500/10 border border-red-500/30 p-5 mt-6 text-sm text-red-200 flex items-start gap-3"><FiInfo className="mt-0.5 shrink-0" /><span>{latest.error_message || "The video could not be made. Please try again."}</span></div>
      )}
      {view && !running && segments.mode === "scenes" && <StudioPanel key={view.id} view={view} segments={segments} clientId={clientId} />}

      <p className="text-xs text-gray-500 text-center mt-6">Do not use someone's face or voice without their permission, and do not use these videos to mislead people. Tell viewers when a presenter is AI-made if it matters to them.</p>

      <div className="mt-12">
        <p className="text-xs uppercase tracking-widest text-white/50 font-semibold mb-4">Your presenter videos</p>
        {loading ? <p className="text-sm text-gray-400">Loading...</p> : history.length === 0 ? <p className="text-sm text-gray-500">Nothing here yet. Your videos will appear here.</p> : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {history.map((job) => {
              let stored = {};
              try { stored = JSON.parse(job.options || "{}"); } catch { /* an old job without options */ }
              return (
                <div key={job.id} className={`rounded-xl bg-white/5 border overflow-hidden ${viewId === job.id ? "border-accent-light/60" : "border-navy-border"}`}>
                  {job.status === "completed" ? (
                    <button type="button" onClick={() => setViewId(job.id)} className="block w-full bg-gradient-to-br from-[#16324f] to-[#0a1a2e] flex justify-center" aria-label={`Open ${job.original_name}`}>
                      {job.images?.[0] ? <img src={job.images[0].thumb} alt="" className="w-full aspect-video object-cover" loading="lazy" /> : <PresenterThumb id={stored.avatar?.presenter} size={150} className="h-[7.5rem] w-auto" />}
                    </button>
                  ) : (
                    <div className="w-full aspect-video flex items-center justify-center text-xs text-gray-500 px-3 text-center bg-navy">
                      {job.status === "failed" ? <span className="text-red-300">{job.error_message}</span> : <span className="inline-flex items-center gap-2"><FiLoader className="animate-spin" /> Working...</span>}
                    </div>
                  )}
                  <div className="p-3">
                    <p className="text-xs text-gray-200 line-clamp-2 min-h-[2rem]" dir="auto">{job.original_name}</p>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-[11px] text-gray-500">{new Date(job.created_at).toLocaleDateString()}</span>
                      {job.status !== "processing" && job.status !== "queued" && <button onClick={() => { if (viewId === job.id) setViewId(null); remove(job); }} title="Delete" className="text-gray-400 hover:text-red-400"><FiTrash2 size={14} /></button>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
